import { useCallback, forwardRef } from "react";
import IconButton from "../IconButton/IconButton";
import styles from "./TableInput.module.scss";
import { Alert, Close, CornerDownLeft } from "../Icon/Icon";
import Button from "../Button/Button";
import Badge from "../Badge/Badge";

interface TableInputProps {
  value: string;
  error?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  index: number;
  totalCount: number;
  addValue: (options?: { value?: string; index?: number }) => void;
  addValues: (value: string[]) => void;
  removeValue: () => void;
  setFocus: (index: number) => void;
}

const TableInput = forwardRef<HTMLInputElement, TableInputProps>(
  function TableInput(
    {
      value,
      onChange,
      index,
      totalCount,
      addValue,
      addValues,
      removeValue,
      setFocus,
      error,
    },
    ref,
  ) {
    const isFirst = index === 0;
    const isLast = index === totalCount - 1;

    const onPaste: React.ClipboardEventHandler<HTMLInputElement> = useCallback(
      (event) => {
        const pasteContent = event.clipboardData.getData("text");
        const pasteContentLines = pasteContent.split("\n").filter(Boolean);

        if (pasteContentLines.length > 1) {
          event.preventDefault();
          event.stopPropagation();

          addValues(pasteContentLines);
        }
      },
      [addValues],
    );

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
      (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();

          addValue({ index: index + 1 });
        }

        if (
          event.key === "Backspace" &&
          event.currentTarget.value === "" &&
          totalCount > 1
        ) {
          event.preventDefault();
          event.stopPropagation();

          removeValue();
          setFocus(index - 1);
        }

        if (event.key === "ArrowUp" && !isFirst) {
          event.preventDefault();
          event.stopPropagation();

          setFocus(index - 1);
        }

        if (event.key === "ArrowDown" && !isLast) {
          event.preventDefault();
          event.stopPropagation();

          setFocus(index + 1);
        }
      },
      [addValue, index, isFirst, isLast, removeValue, setFocus, totalCount],
    );

    return (
      <div className={styles.input}>
        <input
          onChange={onChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          ref={ref}
          value={value}
        />
        <div className={styles.overlay}>
          {error && (
            <span className={styles.error}>
              <Alert size="xsmall" />
              {error}
            </span>
          )}
          <Button
            variant="outline"
            className={styles.addNewButton}
            size="xsmall"
            onClick={() => addValue({ index: index + 1 })}
          >
            <span>Add new</span>
            <Badge
              variant="badge"
              color="default"
              showDot={false}
              size="small"
              className={styles.addNewButtonEnter}
            >
              <CornerDownLeft size="xsmall" />
            </Badge>
          </Button>
          {totalCount > 1 && (
            <IconButton
              aria-label="Delete"
              className={styles.closeButton}
              IconComponent={Close}
              iconSize="xsmall"
              onClick={removeValue}
            />
          )}
        </div>
      </div>
    );
  },
);

export default TableInput;
